import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type CanvasThreadsList } from './types';
import { PRIVATE_CANVAS_THREADS_LIST_KEY } from './usePrivateCanvasThreadsList';

export type UsePrivateUpdateCanvasThreadMutationParams = NonNullable<Parameters<typeof Canvases.Threads.update>[0]>;

export const usePrivateUpdateCanvasThreadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UsePrivateUpdateCanvasThreadMutationParams) =>
      callAirApi(() => Canvases.Threads.update(params)),
    onError: (error) => {
      reportErrorToBugsnag({
        context: 'Failed to update canvas thread',
        error,
      });
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: [PRIVATE_CANVAS_THREADS_LIST_KEY] });

      queryClient.setQueriesData<InfiniteData<CanvasThreadsList>>(
        { queryKey: [PRIVATE_CANVAS_THREADS_LIST_KEY] },
        (old) => {
          if (!old) {
            return old;
          }

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((thread) => (thread.id === params.id ? { ...thread, ...params } : thread)),
            })),
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PRIVATE_CANVAS_THREADS_LIST_KEY] });
    },
  });
};
